import { Skeleton } from 'antd'
import { observer } from 'mobx-react-lite'
import { lazy, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftOutlined } from '@ant-design/icons'

import { useStore } from 'store/useStore'
import { ContainerLK } from 'sections/LKSection/shared/components/ContainerLK/ContainerLK'
import { Margin, Padding } from 'components/ui'
import { Button, Checkbox, Flex, Link, Paragraph, Text, Title } from 'components/uiKit'
import { thousandsWhiteSpacing } from 'utils/formatting/thousandsWhiteSpacing/thousandsWhiteSpacing'
import { getOrderItemRoute } from 'utils/helpers/routes/getRoutes'
import { useAsyncEffect } from 'utils/hooks/useAsyncEffect'
import { colors } from 'shared/constants/values'
import { CurrencyFromCountry } from 'shared/types/types'

import { OrderPaymentStatus, OrderStatus } from '../../types'

import s from './OrderPaymentPage.module.scss'

const OrderPaymentPage = observer(() => {
  const { viewOrderUiStore: uiStore, appStore } = useStore()

  const { orderId } = useParams()
  const navigate = useNavigate()

  const [agreed, setAgreed] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isPaid, setIsPaid] = useState(false)

  const order = uiStore.selectedOrder

  useAsyncEffect(async () => {
    setLoading(true)

    if (!order && orderId) {
      await uiStore.getOrderList()
      uiStore.setSelectedOrder(uiStore.getOrder(+orderId))
    }

    if (uiStore.selectedOrder && uiStore.selectedOrder.status === OrderStatus.waitingPayment) {
      const id = uiStore.selectedOrder.id || orderId

      if (id) {
        const res = await uiStore.checkOrderPayment(+id)

        if (res?.status === OrderPaymentStatus.paid || res?.status === OrderPaymentStatus.pending) {
          setIsPaid(true)
        }
      }
    }

    orderId && (await uiStore.addOrderPayment(+orderId))
    setLoading(false)
  }, [orderId])

  return (
    <ContainerLK>
      <Padding py={20} className={s.OrderPaymentPage}>
        <Flex className={s.wrap} vertical>
          <Flex gap={16} align="center">
            <Button
              shape="circle"
              icon={<ArrowLeftOutlined />}
              onClick={() => orderId && navigate(getOrderItemRoute(+orderId))}
            />
            <Title level={3}>Оплата гарантийного взноса по заявке № {orderId}</Title>
          </Flex>

          <Margin mt={24}>
            <Skeleton loading={loading}>
              {!isPaid ? (
                <>
                  <Paragraph>
                    Заявка на открытие нового ПВЗ создана и ожидает оплаты. На оплату у Вас есть 24 часа, далее заявка
                    будет удалена, как неоплаченная.
                  </Paragraph>
                  <Paragraph>
                    Если заявку еще не одобрили и отправили на доработку, у Вас есть 5 дней, чтобы добавить
                    дополнительную информацию по запросу в заявку.
                  </Paragraph>
                  <Paragraph>Если информация не будет предоставлена в срок, заявка будет удалена.</Paragraph>
                  <Paragraph>
                    После активации ПВЗ, сумма, уплаченная за заявку, будет зачислена на баланс ПВЗ. Если Вы не
                    активируете ПВЗ по оплаченной заявке в срок 30 дней со дня создания заявки, оплата за заявку
                    возвращена Вам не будет. Если после оплаты Вам отказали в открытии и отклонили заявку – денежные
                    средства будут возвращены.
                  </Paragraph>
                </>
              ) : (
                <Paragraph>
                  {uiStore.orderPaymentStatus === OrderPaymentStatus.paid ? 'Заявка оплачена' : 'Оплата в обработке'}
                </Paragraph>
              )}

              <Flex vertical gap={16} align="flex-start">
                <Flex vertical>
                  <Text strong>Партнер</Text>
                  <Text>{appStore.company?.orgName ?? '–'}</Text>
                </Flex>

                <Flex vertical>
                  <Text strong>ИНН</Text>
                  <Text>{appStore.company?.inn ?? '–'}</Text>
                </Flex>

                {uiStore.orderPaymentInfo && (
                  <Flex vertical>
                    <Text strong>Сумма</Text>
                    <Text>
                      {thousandsWhiteSpacing(uiStore.orderPaymentInfo.data.sum)}{' '}
                      {CurrencyFromCountry[uiStore.orderPaymentInfo.data.country]}
                    </Text>
                  </Flex>
                )}

                {!isPaid && (
                  <>
                    <Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)}>
                      <Text strong style={{ color: agreed ? colors.corp : colors.secondary }}>
                        Согласен
                      </Text>
                    </Checkbox>

                    <Link href={uiStore.orderPaymentInfo?.paymentUrl}>
                      <Button disabled={!agreed}>Перейти к оплате</Button>
                    </Link>
                  </>
                )}
              </Flex>
            </Skeleton>
          </Margin>
        </Flex>
      </Padding>
    </ContainerLK>
  )
})

export default OrderPaymentPage

export const OrderPaymentPageAsync = lazy(() => import('./OrderPaymentPage'))
